import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Table } from '@components/Common';
import CheckMark from '@components/Common/CheckMark';
import { ContentContainer } from '@components/Common/Loaders/ContentLoader';
import OperationLoader from '@components/Common/Loaders/OperationLoader';
import { HeadingRow, Layout, MainContent, SideBar } from '@components/Common/SideBarLayout';
import { matchSorter } from 'match-sorter';
import { TFunction } from '~types/types';
import CustomerEditor from './CustomerEditor';

// TanStack Query hooks for Customers
import {
  useCustomersQuery,
  useCreateCustomerMutation,
  useUpdateCustomerMutation,
  useDeleteCustomerMutation,
  Customer,
  EditCustomerCmd,
  CreateCustomerCmd
} from '@api/queries/customers';

// Re-export Customer type for other components
export type { Customer, EditCustomerCmd, CreateCustomerCmd } from '@api/queries/customers';

const createBooleanColumn = (t: TFunction, header: string, accessor: string, forceWidth: boolean = false) => ({
  Header: header,
  id: accessor,
  width: forceWidth ? 70 : undefined,
  minWidth: 70,
  accessor,
  Cell: ({ value }) => <CheckMark active={value} />,
  filterMethod: (filter, row) => (filter.value !== '' ? row[filter.id] === (filter.value === 'true') : true),
  Filter: ({ filter, onChange }) => (
    <select
      onChange={event => onChange(event.target.value)}
      style={{ width: '100%' }}
      value={filter ? filter.value : ''}
    >
      <option value="" />
      <option value="true">{t('customer.filter_active')}</option>
      <option value="false">{t('customer.filter_inactive')}</option>
    </select>
  )
});

const CustomersList: React.FC = () => {
  const { t } = useTranslation();

  // Local UI state for editor
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

  // TanStack Query for customers
  const { data: customers = [], isLoading } = useCustomersQuery();
  const createCustomerMutation = useCreateCustomerMutation();
  const updateCustomerMutation = useUpdateCustomerMutation();
  const deleteCustomerMutation = useDeleteCustomerMutation();

  const isSaving = createCustomerMutation.isPending || updateCustomerMutation.isPending;
  const isRemoving = deleteCustomerMutation.isPending;

  const openEditor = (customer: Customer | null = null) => {
    setSelectedCustomer(customer);
    setIsEditorOpen(true);
  };

  const closeEditor = () => {
    setIsEditorOpen(false);
    setSelectedCustomer(null);
  };

  const saveCustomer = (customerData: EditCustomerCmd | CreateCustomerCmd) => {
    if (selectedCustomer) {
      // Update existing customer
      updateCustomerMutation.mutate(
        {
          id: selectedCustomer.id,
          ...customerData
        } as EditCustomerCmd,
        { onSuccess: () => closeEditor() }
      );
    } else {
      // Create new customer
      createCustomerMutation.mutate(
        customerData as CreateCustomerCmd,
        { onSuccess: () => closeEditor() }
      );
    }
  };

  const removeCustomer = () => {
    if (selectedCustomer) {
      deleteCustomerMutation.mutate(selectedCustomer.id, {
        onSuccess: () => closeEditor()
      });
    }
  };

  const formKey = (selectedCustomer && selectedCustomer.id) || 'new';

  return (
    <>
      <HeadingRow>
        <Button primary={true} onClick={() => openEditor()}>
          {t('add')} {t('customer.customer').toLowerCase()}
        </Button>
        {isLoading && <OperationLoader title={t('loading') + '...'} />}
      </HeadingRow>
      <Layout>
        <MainContent>
          <ContentContainer>
            <Table
              data={customers}
              columns={[
                {
                  Header: 'Id',
                  id: 'id',
                  accessor: 'id',
                  show: false
                },
                {
                  Header: t('customer.customer_number'),
                  id: 'customerNumber',
                  accessor: 'customerNumber',
                  width: 100
                },
                {
                  Header: t('customer.name'),
                  id: 'name',
                  accessor: 'name',
                  filterMethod: (filter, rows) => matchSorter(rows, filter.value, { keys: ['name'] }),
                  filterAll: true
                },
                {
                  Header: t('customer.city'),
                  id: 'city',
                  accessor: 'city',
                  maxWidth: 150
                },
                {
                  Header: t('customer.notes'),
                  id: 'notes',
                  accessor: 'notes'
                },
                {
                  Header: t('customer.external_customer_id'),
                  id: 'externalCustomerId',
                  accessor: 'externalCustomerId',
                  maxWidth: 150
                },
                createBooleanColumn(t, t('customer.summaries'), 'isSurfaceRadarNotificationEnabled'),
                createBooleanColumn(t, t('customer.is_entry_sending_enabled'), 'isEntrySendingEnabled'),
                createBooleanColumn(t, t('customer.set_reference_from_truck_scale'), 'setReferenceFromTruckScale'),
                createBooleanColumn(t, t('customer.active'), 'isActive', true)
              ]}
              defaultSortColumn="name"
              inactiveRowSelector={rowInfo => !rowInfo.original.isActive}
              onRowSelected={(rowInfo: any) => openEditor(rowInfo.original)}
              rowSelector={i => isEditorOpen && selectedCustomer && selectedCustomer.id === i.original.id}
              defaultPageSize={25}
            />
          </ContentContainer>
        </MainContent>
        {isEditorOpen && (
          <SideBar>
            <CustomerEditor
              key={formKey}
              customer={selectedCustomer}
              save={saveCustomer}
              close={closeEditor}
              remove={selectedCustomer ? removeCustomer : undefined}
              isSaving={isSaving}
              isRemoving={isRemoving}
            />
          </SideBar>
        )}
      </Layout>
    </>
  );
};

export default CustomersList;
